import React, { useState, useRef, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import PersonIcon from "@material-ui/icons/Person";
import ReceiptIcon from "@material-ui/icons/Receipt";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import BusinessIcon from "@material-ui/icons/Business";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import { client } from "../../service/ApolloClient/ApolloClient";
import { QUERY_GLOBAL_SEARCH } from "../../service/ApolloClient/ApolloQueries";
import { withRouter } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  input: {
    backgroundColor: "white",
    borderRadius: 5,
  },
  optionItem: {
    padding: "8px 16px",
  },
  categoryHeader: {
    padding: "6px 16px",
    backgroundColor: "#f5f5f5",
    display: "flex",
    alignItems: "center",
  },
  categoryText: {
    fontWeight: "bold",
    fontSize: "0.85rem",
    color: "#555",
  },
  resultTitle: {
    fontWeight: 500,
  },
  resultSubtitle: {
    fontSize: "0.8rem",
    color: "#888",
    marginTop: 2,
  },
}));

const ENTITY_TYPES = {
  user: { label: "اشخاص", icon: PersonIcon, path: "/person/edit/" },
  cheque: { label: "چک‌ها", icon: ReceiptIcon, path: "/cheque/edit/" },
  car_contract: { label: "قرارداد خودرو", icon: DirectionsCarIcon, path: "/car-contract/edit/" },
  legal_entity: { label: "اشخاص حقوقی", icon: BusinessIcon, path: "/legal-entity/edit/" },
  bank_account: { label: "حساب‌های بانکی", icon: AccountBalanceIcon, path: "/bank-account/edit/" },
  debt_credit: { label: "بدهی و طلب", icon: AccountBalanceIcon, path: "/debit-credit/edit/" },
};

const GlobalSearch = (props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchResults = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    client
      .query({
        query: QUERY_GLOBAL_SEARCH,
        variables: { search: searchTerm, limit: 5 },
        fetchPolicy: "network-only",
      })
      .then((response) => {
        if (response.data && response.data.globalSearch) {
          setOptions(response.data.globalSearch);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const groupByType = (option) => {
    const config = ENTITY_TYPES[option.type];
    return config ? config.label : option.type;
  };

  const getOptionLabel = (option) => {
    return option.title || "";
  };

  const getOptionSelected = (option, value) => {
    return option.type === value.type && option.id === value.id;
  };

  const navigateToResult = (result) => {
    if (!result) return;
    const config = ENTITY_TYPES[result.type];
    if (config) {
      props.history.push(config.path + result.id);
    }
    setKeyword("");
    setOpen(false);
  };

  const getIcon = (type) => {
    const config = ENTITY_TYPES[type];
    if (!config) return null;
    const IconComponent = config.icon;
    return <IconComponent fontSize="small" style={{ color: "#666" }} />;
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      groupBy={groupByType}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      onChange={(event, newValue) => navigateToResult(newValue)}
      loading={loading}
      loadingText={"در حال جستجو..."}
      noOptionsText={
        keyword.trim().length > 0 && !loading ? "نتیجه‌ای یافت نشد" : "عبارت جستجو را وارد کنید"
      }
      renderGroup={(params) => (
        <li key={params.key}>
          <Box className={classes.categoryHeader}>
            <Typography className={classes.categoryText}>
              {params.group}
            </Typography>
          </Box>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(option) => (
        <Box className={classes.optionItem} display="flex" alignItems="center">
          <ListItemIcon style={{ minWidth: 36 }}>
            {getIcon(option.type)}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography className={classes.resultTitle}>
                {option.title}
              </Typography>
            }
            secondary={
              option.subtitle &&
              !(typeof option.subtitle === "string" &&
                /^\[\s*\]$/.test(option.subtitle.trim())) ? (
                <Typography className={classes.resultSubtitle}>
                  {option.subtitle}
                </Typography>
              ) : null
            }
          />
        </Box>
      )}
      inputValue={keyword}
      onInputChange={(event, value, reason) => {
        if (reason === "input") {
          setKeyword(value);
          if (debounceRef.current) clearTimeout(debounceRef.current);
          if (!value || value.trim().length === 0) {
            setOptions([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          debounceRef.current = setTimeout(() => {
            fetchResults(value);
          }, 400);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          className={classes.input}
          placeholder={"جستجوی سراسری..."}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#999" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default withRouter(GlobalSearch);
